import type { BattleSnapshot, ReplayFile } from '../replay/replay';
import type { BattleResult, EntityId } from '../engine/types';
import type { DiagnosticJournal, DiagnosticRecord } from './journal';
import { ASSET_PACK_VERSION } from '../content/asset-pack';

export interface AnalyzerFinding {
  ruleId: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  tickFrom: number;
  tickTo: number;
  entityIds: readonly EntityId[];
  evidenceEventIds: readonly number[];
  message: string;
  suggestedArea: 'engine' | 'content' | 'bot' | 'renderer' | 'audio' | 'performance';
}

export interface MatchMetrics {
  records: number;
  commandsAccepted: number;
  commandsRejected: number;
  damageEvents: number;
  entitiesSpawned: number;
  entitiesDied: number;
  botDecisions: number;
  presentationWarnings: number;
  pathReplans: number;
  navigationStepRejections: number;
  routeOscillations: number;
  stuckFindings: number;
}

export interface MatchDiagnosticBundle {
  manifest: {
    formatVersion: 1;
    matchId: string;
    createdAt: string;
    engineVersion: string;
    contentVersion: string;
    assetPackVersion: string;
    rulesetId: string;
    arenaId: string;
    seed: number;
    logProfile: string;
    environment: { runtime: 'browser' | 'node'; userAgent: string; language: string; viewport: string | null };
    completeness: { droppedRecords: number; sinkErrors: number };
  };
  replay: ReplayFile;
  records: readonly DiagnosticRecord[];
  checkpoints: readonly BattleSnapshot[];
  metrics: MatchMetrics;
  findings: readonly AnalyzerFinding[];
  summary: { result: BattleResult | null; critical: number; errors: number; warnings: number };
}

const finding = (
  ruleId: string,
  severity: AnalyzerFinding['severity'],
  record: DiagnosticRecord,
  message: string,
  suggestedArea: AnalyzerFinding['suggestedArea'],
): AnalyzerFinding => ({
  ruleId,
  severity,
  tickFrom: record.tick,
  tickTo: record.tick,
  entityIds: record.entityIds ?? [],
  evidenceEventIds: [record.eventId],
  message,
  suggestedArea,
});

const COMBAT_ATTRIBUTION_CODES = new Set([
  'ENTITY_SPAWNED',
  'ATTACK_STARTED',
  'ATTACK_RELEASED',
  'ATTACK_CANCELLED',
  'PROJECTILE_SPAWNED',
  'PROJECTILE_IMPACT',
  'PROJECTILE_CANCELLED',
  'DAMAGE_APPLIED',
  'ENTITY_DIED',
  'MECHANIC_START',
  'MECHANIC_IMPACT',
  'MECHANIC_COMPLETE',
  'MECHANIC_CANCELLED',
]);

export function analyzeRecords(records: readonly DiagnosticRecord[], droppedRecords = 0, sinkErrors = 0): { findings: AnalyzerFinding[]; metrics: MatchMetrics } {
  const findings: AnalyzerFinding[] = [];
  const metrics: MatchMetrics = {
    records: records.length,
    commandsAccepted: 0,
    commandsRejected: 0,
    damageEvents: 0,
    entitiesSpawned: 0,
    entitiesDied: 0,
    botDecisions: 0,
    presentationWarnings: 0,
    pathReplans: 0,
    navigationStepRejections: 0,
    routeOscillations: 0,
    stuckFindings: 0,
  };
  const deaths = new Set<number>();
  const lastPositions = new Map<number, { tick: number; x: number; y: number; moving: boolean }>();
  const targetChanges = new Map<number, number[]>();
  const routeChanges = new Map<number, Array<{ tick: number; bridgeId: string | null }>>();
  const commandResults = new Map<string, DiagnosticRecord>();
  const commandMutations = new Set<string>();
  const entityMovements = new Map<number, string>();
  const invalidRiverEntities = new Set<number>();
  const pendingAttacks = new Map<number, DiagnosticRecord>();
  const liveProjectiles = new Map<number, DiagnosticRecord>();
  const liveMechanics = new Map<string, DiagnosticRecord>();
  const missingCombatAttribution: DiagnosticRecord[] = [];
  const audioPlaybackFailures: DiagnosticRecord[] = [];
  const unlockedDeploymentZones = new Set<string>();
  let botStrategySnapshot: { modelHash?: string; selectedDeck?: string[] } | null = null;
  let botDeckSelected = false;
  let playerModelUpdates = 0;
  type LoggedGeometry = { riverTop: number; riverBottom: number; bridges: Array<{ id: string; x: number; width: number }> };
  let geometry: LoggedGeometry | null = null;
  let previousEventId = 0;
  for (const record of records) {
    if (record.eventId <= previousEventId) findings.push(finding('EVENT_ORDER_INVALID', 'critical', record, 'Diagnostic event IDs are not monotonic', 'engine'));
    previousEventId = record.eventId;
    if (COMBAT_ATTRIBUTION_CODES.has(record.code) && !record.cardIds?.length) missingCombatAttribution.push(record);
    if (record.code === 'COMMAND_ACCEPTED') metrics.commandsAccepted += 1;
    if (record.code === 'MATCH_CREATED') {
      const candidate: LoggedGeometry | undefined = (record.payload as { geometry?: LoggedGeometry }).geometry;
      if (candidate && Number.isFinite(candidate.riverTop) && Number.isFinite(candidate.riverBottom) && Array.isArray(candidate.bridges)) geometry = candidate;
    }
    if (record.code === 'COMMAND_REJECTED') metrics.commandsRejected += 1;
    if (record.code === 'DEPLOYMENT_ZONE_UNLOCKED') {
      const zoneId = (record.payload as { zoneId?: string }).zoneId;
      if (zoneId && unlockedDeploymentZones.has(zoneId)) findings.push(finding('DEPLOYMENT_ZONE_DUPLICATE_UNLOCK', 'error', record, `Deployment zone ${zoneId} was unlocked more than once`, 'engine'));
      if (zoneId) unlockedDeploymentZones.add(zoneId);
    }
    if (record.code === 'COMMAND_ACCEPTED' && record.commandSequence !== undefined) {
      const payload = record.payload as { command?: { playerId?: string } };
      const key = `${payload.command?.playerId ?? 'unknown'}:${record.commandSequence}`;
      const previous = commandResults.get(key);
      if (previous) findings.push(finding('COMMAND_RESULT_INCONSISTENT', 'error', record, `Accepted command ${key} has more than one result`, 'engine'));
      else commandResults.set(key, record);
    }
    if (record.code === 'CARD_PLAYED' && record.commandSequence !== undefined) {
      const payload = record.payload as { playerId?: string };
      commandMutations.add(`${payload.playerId ?? 'unknown'}:${record.commandSequence}`);
    }
    if (record.code === 'DAMAGE_APPLIED') metrics.damageEvents += 1;
    if (record.code === 'ENTITY_SPAWNED') {
      metrics.entitiesSpawned += 1;
      const id = record.entityIds?.[0];
      const movement = (record.payload as { movement?: string }).movement;
      if (id !== undefined && movement) entityMovements.set(id, movement);
    }
    if (record.code === 'ENTITY_DIED') {
      metrics.entitiesDied += 1;
      const id = record.entityIds?.[0];
      if (id !== undefined && deaths.has(id)) findings.push(finding('DUPLICATE_DEATH', 'error', record, `Entity ${id} died more than once`, 'engine'));
      if (id !== undefined) deaths.add(id);
    }
    if (record.code === 'BOT_DECISION') metrics.botDecisions += 1;
    if (record.phase === 'presentation' && (record.level === 'warn' || record.level === 'error')) metrics.presentationWarnings += 1;
    if (record.code === 'STATE_NON_FINITE') findings.push(finding('STATE_NON_FINITE', 'critical', record, 'Non-finite state value detected', 'engine'));
    if (record.code === 'ELIXIR_OUT_OF_RANGE' || record.code === 'HP_OUT_OF_RANGE' || record.code === 'ENTITY_OUT_OF_BOUNDS' || record.code === 'ORPHAN_PROJECTILE' || record.code === 'ATTACK_TIMING_INVALID') findings.push(finding(record.code, 'error', record, record.code.replaceAll('_', ' ').toLowerCase(), 'engine'));
    if (record.code === 'DET_CHECKSUM_MISMATCH') findings.push(finding('DET_CHECKSUM_MISMATCH', 'critical', record, 'Live and replay checksums differ', 'engine'));
    if (record.code === 'REPLAY_RESULT_MISMATCH') findings.push(finding('REPLAY_RESULT_MISMATCH', 'critical', record, 'Migrated replay produced a different result', 'engine'));
    if (record.code === 'ILLEGAL_TARGET') findings.push(finding('ILLEGAL_TARGET', 'error', record, 'Entity selected an illegal target', 'engine'));
    if (record.code === 'MISSING_CLIP' || record.code === 'ASSET_FALLBACK' || record.code === 'MISSING_AUDIO') findings.push(finding(record.code, 'warning', record, record.code.replaceAll('_', ' ').toLowerCase(), record.code === 'MISSING_AUDIO' ? 'audio' : 'renderer'));
    if (record.code === 'AUDIO_PLAYBACK_BLOCKED') audioPlaybackFailures.push(record);
    if (record.code === 'TICK_OVERRUN') {
      const payload = record.payload as { elapsedMs?: number; budgetMs?: number };
      const budgetMs = payload.budgetMs ?? 1_000 / 30;
      if ((payload.elapsedMs ?? Number.POSITIVE_INFINITY) > budgetMs) findings.push(finding('TICK_OVERRUN', 'warning', record, `Engine tick exceeded its ${budgetMs.toFixed(1)} ms budget`, 'performance'));
    }
    if (record.code === 'FRAME_DROP_BURST') {
      const payload = record.payload as { frameMs?: number; maxFrameMs?: number; count?: number; visibilityState?: string };
      const maxFrameMs = payload.maxFrameMs ?? payload.frameMs ?? 0;
      if (maxFrameMs > 80 && payload.visibilityState !== 'hidden') {
        const count = payload.count ?? 1;
        findings.push(finding('FRAME_DROP_BURST', maxFrameMs >= 200 ? 'warning' : 'info', record, `${count} slow frame${count === 1 ? '' : 's'}; maximum ${maxFrameMs.toFixed(1)} ms`, 'performance'));
      }
    }
    if (record.code === 'GROUND_SEPARATION_CLAMPED') {
      const payload = record.payload as { beforeX?: number; afterX?: number; radius?: number };
      const correction = Math.abs((payload.afterX ?? 0) - (payload.beforeX ?? 0));
      const threshold = Math.max(500, (payload.radius ?? 0) * 2);
      if (Number.isFinite(payload.beforeX) && Number.isFinite(payload.afterX) && correction > threshold) {
        findings.push(finding('GROUND_SEPARATION_TELEPORT', 'error', record, `Ground separation caused a ${correction} unit bridge correction (limit ${threshold})`, 'engine'));
      }
    }
    if (record.code === 'GROUND_ROUTE_CONSTRAINED') findings.push(finding('GROUND_ROUTE_RECOVERED', 'warning', record, 'Ground unit was pushed outside its bridge corridor and recovered', 'engine'));
    if (record.code === 'PATH_REPLANNED') metrics.pathReplans += 1;
    if (record.code === 'NAVIGATION_STEP_REJECTED') metrics.navigationStepRejections += 1;
    if (record.code === 'BOT_ILLEGAL_ACTION') findings.push(finding('BOT_ILLEGAL_ACTION', 'error', record, 'Bot produced an illegal command', 'bot'));
    if (record.code === 'BOT_DECK_SELECTED') {
      botDeckSelected = true;
      const payload = record.payload as { deck?: string[] };
      if (!Array.isArray(payload.deck) || payload.deck.length !== 8 || new Set(payload.deck).size !== 8) findings.push(finding('BOT_DECK_INVALID', 'error', record, 'Bot deck policy did not select eight unique cards', 'bot'));
    }
    if (record.code === 'BOT_STRATEGY_SNAPSHOT_CREATED') {
      if (botStrategySnapshot) findings.push(finding('BOT_STRATEGY_DUPLICATE', 'error', record, 'Bot strategy snapshot was created more than once', 'bot'));
      botStrategySnapshot = record.payload as { modelHash?: string; selectedDeck?: string[] };
      if (!Array.isArray(botStrategySnapshot.selectedDeck) || botStrategySnapshot.selectedDeck.length !== 8 || new Set(botStrategySnapshot.selectedDeck).size !== 8) findings.push(finding('BOT_STRATEGY_INVALID', 'error', record, 'Bot strategy snapshot contains an invalid deck', 'bot'));
    }
    if (record.code === 'PLAYER_MODEL_UPDATED') {
      playerModelUpdates += 1;
      if (playerModelUpdates > 1) findings.push(finding('PLAYER_MODEL_MULTIPLE_UPDATES', 'error', record, 'Player learning model was updated more than once in one match', 'bot'));
    }
    if (record.code === 'BOT_DECISION') {
      const payload = record.payload as { input?: Record<string, unknown>; selected?: unknown; strategySnapshotHash?: string };
      if (payload.input && Object.keys(payload.input).some((key) => !['elixirMilli', 'hand', 'activeDeploymentZoneIds', 'visibleEntities', 'visibleEntityCount', 'visibleEntityHash'].includes(key))) findings.push(finding('BOT_HIDDEN_INFO_ACCESS', 'error', record, 'Bot decision input contains hidden fields', 'bot'));
      if ((payload.input?.elixirMilli as number) >= 10_000 && !payload.selected) findings.push(finding('BOT_IDLE_AT_CAP', 'warning', record, 'Bot waited at maximum elixir', 'bot'));
      if (botStrategySnapshot?.modelHash && payload.strategySnapshotHash && payload.strategySnapshotHash !== botStrategySnapshot.modelHash) findings.push(finding('BOT_STRATEGY_HASH_MISMATCH', 'error', record, 'Bot decision used a different strategy snapshot hash', 'bot'));
    }
    if (record.code === 'TARGET_CHANGED') {
      const id = record.entityIds?.[0];
      if (id !== undefined) {
        const changes = targetChanges.get(id) ?? [];
        changes.push(record.tick);
        targetChanges.set(id, changes.filter((tick) => record.tick - tick <= 90));
        if ((targetChanges.get(id)?.length ?? 0) >= 8) findings.push(finding('TARGET_THRASHING', 'warning', record, `Entity ${id} changed target at least 8 times in 90 ticks`, 'engine'));
      }
    }
    if (record.code === 'ATTACK_STARTED') {
      const sourceId = record.entityIds?.[0];
      if (sourceId !== undefined) {
        const previous = pendingAttacks.get(sourceId);
        if (previous) findings.push(finding('ATTACK_LIFECYCLE_OVERLAP', 'error', record, `Entity ${sourceId} started a second attack before closing the previous one`, 'engine'));
        pendingAttacks.set(sourceId, record);
      }
    }
    if (record.code === 'ATTACK_RELEASED' || record.code === 'ATTACK_CANCELLED') {
      const sourceId = record.entityIds?.[0];
      if (sourceId !== undefined) pendingAttacks.delete(sourceId);
    }
    if (record.code === 'PROJECTILE_SPAWNED') {
      const payload = record.payload as { projectileId?: number; spellId?: string };
      const projectileId = payload.projectileId ?? (payload.spellId ? record.entityIds?.[0] : record.entityIds?.at(-1));
      if (projectileId !== undefined) liveProjectiles.set(projectileId, record);
    }
    if (record.code === 'PROJECTILE_IMPACT' || record.code === 'PROJECTILE_CANCELLED') {
      const projectileId = record.entityIds?.[0];
      if (projectileId !== undefined) liveProjectiles.delete(projectileId);
    }
    if (record.code === 'MECHANIC_START') {
      const mechanic = (record.payload as { mechanic?: string }).mechanic ?? 'unknown';
      const key = `${record.entityIds?.[0] ?? 'none'}:${mechanic}`;
      if (liveMechanics.has(key)) findings.push(finding('MECHANIC_LIFECYCLE_OVERLAP', 'error', record, `Mechanic ${key} started before the previous operation completed`, 'engine'));
      liveMechanics.set(key, record);
    }
    if (record.code === 'MECHANIC_COMPLETE' || record.code === 'MECHANIC_CANCELLED') {
      const mechanic = (record.payload as { mechanic?: string }).mechanic ?? 'unknown';
      liveMechanics.delete(`${record.entityIds?.[0] ?? 'none'}:${mechanic}`);
    }
    if (record.code === 'ENTITY_DIED') {
      const sourceId = record.entityIds?.[0];
      if (sourceId !== undefined && pendingAttacks.has(sourceId)) {
        findings.push(finding('ATTACK_LIFECYCLE_INCOMPLETE', 'error', record, `Entity ${sourceId} died without cancelling its pending attack`, 'engine'));
        pendingAttacks.delete(sourceId);
      }
    }
    if (record.code === 'BATTLE_ENDED') {
      for (const sourceId of pendingAttacks.keys()) findings.push(finding('ATTACK_LIFECYCLE_INCOMPLETE', 'error', record, `Entity ${sourceId} still had a pending attack when the match ended`, 'engine'));
      for (const projectileId of liveProjectiles.keys()) findings.push(finding('PROJECTILE_LIFECYCLE_INCOMPLETE', 'error', record, `Projectile ${projectileId} was still active when the match ended`, 'engine'));
      for (const key of liveMechanics.keys()) findings.push(finding('MECHANIC_LIFECYCLE_INCOMPLETE', 'error', record, `Mechanic ${key} was still active when the match ended`, 'engine'));
      pendingAttacks.clear();
      liveProjectiles.clear();
      liveMechanics.clear();
    }
    if (record.code === 'ROUTE_CHANGED') {
      const id = record.entityIds?.[0];
      const bridgeId = (record.payload as { bridgeId?: string | null }).bridgeId ?? null;
      if (id !== undefined && bridgeId !== null) {
        const changes = routeChanges.get(id) ?? [];
        changes.push({ tick: record.tick, bridgeId });
        const recent = changes.filter((item) => record.tick - item.tick <= 120);
        routeChanges.set(id, recent);
        if (recent.length >= 4 && new Set(recent.map((item) => item.bridgeId)).size > 1) {
          metrics.routeOscillations += 1;
          findings.push(finding('ROUTE_OSCILLATION', 'warning', record, `Entity ${id} oscillates between routes`, 'engine'));
        }
      }
    }
    if (record.code === 'ENTITY_POSITION') {
      const id = record.entityIds?.[0];
      const payload = record.payload as { x?: number; y?: number; movement?: string; radius?: number; bridgeId?: string | null; moving?: boolean; attacking?: boolean };
      if (id !== undefined && Number.isFinite(payload.x) && Number.isFinite(payload.y)) {
        const movement = payload.movement ?? entityMovements.get(id);
        if (geometry && movement === 'ground' && payload.y! >= geometry.riverTop && payload.y! <= geometry.riverBottom) {
          const radius = Math.max(0, payload.radius ?? 0);
          const validBridge = geometry.bridges.find((bridge) => {
            const inset = Math.min(radius, Math.max(0, bridge.width / 2 - 40));
            return payload.x! >= bridge.x - bridge.width / 2 + inset && payload.x! <= bridge.x + bridge.width / 2 - inset;
          });
          if (!validBridge && !invalidRiverEntities.has(id)) {
            findings.push(finding('INVALID_RIVER_CROSSING', 'error', record, `Ground entity ${id} entered the river outside a bridge corridor`, 'engine'));
            invalidRiverEntities.add(id);
          } else if (validBridge) invalidRiverEntities.delete(id);
        } else if (id !== undefined) invalidRiverEntities.delete(id);
        const previous = lastPositions.get(id);
        if (!payload.moving || payload.attacking) {
          lastPositions.set(id, { tick: record.tick, x: payload.x!, y: payload.y!, moving: false });
        } else if (previous?.moving && previous.x === payload.x && previous.y === payload.y && record.tick - previous.tick > 150) {
          metrics.stuckFindings += 1;
          findings.push(finding('UNIT_STUCK', 'warning', record, `Entity ${id} has not moved for more than 150 ticks`, 'engine'));
        } else if (!previous?.moving || previous.x !== payload.x || previous.y !== payload.y) {
          lastPositions.set(id, { tick: record.tick, x: payload.x!, y: payload.y!, moving: true });
        }
      }
    }
  }
  if (botDeckSelected && !botStrategySnapshot) findings.push({
    ruleId: 'BOT_STRATEGY_MISSING',
    severity: 'error',
    tickFrom: 0,
    tickTo: 0,
    entityIds: [],
    evidenceEventIds: [],
    message: 'Bot deck was selected without a strategy snapshot',
    suggestedArea: 'bot',
  });
  for (const [key, record] of commandResults) {
    if (record.code === 'COMMAND_ACCEPTED' && !commandMutations.has(key)) findings.push(finding('COMMAND_RESULT_INCONSISTENT', 'error', record, `Accepted command ${key} did not mutate on its due tick`, 'engine'));
  }
  if (audioPlaybackFailures.length > 0) {
    const first = audioPlaybackFailures[0]!;
    const last = audioPlaybackFailures.at(-1)!;
    const attempts = audioPlaybackFailures.reduce((total, record) => total + ((record.payload as { count?: number }).count ?? 1), 0);
    const cues = new Set(audioPlaybackFailures.map((record) => (record.payload as { cue?: string }).cue).filter((cue): cue is string => Boolean(cue)));
    findings.push({
      ruleId: 'AUDIO_PLAYBACK_BLOCKED',
      severity: 'warning',
      tickFrom: first.tick,
      tickTo: last.tick,
      entityIds: [],
      evidenceEventIds: audioPlaybackFailures.map((record) => record.eventId),
      message: `${attempts} playback attempts across ${cues.size} cue${cues.size === 1 ? '' : 's'} failed`,
      suggestedArea: 'audio',
    });
  }
  if (missingCombatAttribution.length > 0) {
    const first = missingCombatAttribution[0]!;
    const last = missingCombatAttribution.at(-1)!;
    const counts = missingCombatAttribution.reduce((result, record) => result.set(record.code, (result.get(record.code) ?? 0) + 1), new Map<string, number>());
    findings.push({
      ruleId: 'COMBAT_ATTRIBUTION_MISSING',
      severity: 'error',
      tickFrom: first.tick,
      tickTo: last.tick,
      entityIds: [...new Set(missingCombatAttribution.flatMap((record) => record.entityIds ?? []))],
      evidenceEventIds: missingCombatAttribution.map((record) => record.eventId),
      message: `${missingCombatAttribution.length} combat lifecycle record(s) are missing source cardIds: ${[...counts].map(([code, count]) => `${code}=${count}`).join(', ')}`,
      suggestedArea: 'engine',
    });
  }
  if (droppedRecords > 0 || sinkErrors > 0) {
    const last = records.at(-1) ?? ({ eventId: 0, tick: 0, entityIds: [] } as unknown as DiagnosticRecord);
    findings.push(finding('LOG_INCOMPLETE', 'error', last, `${droppedRecords} records dropped; ${sinkErrors} sink errors`, 'engine'));
  }
  return { findings, metrics };
}

export function createDiagnosticBundle(options: {
  journal: DiagnosticJournal;
  replay: ReplayFile;
  checkpoints: readonly BattleSnapshot[];
  result: BattleResult | null;
  engineVersion: string;
  contentVersion: string;
  arenaId: string;
  seed: number;
}): MatchDiagnosticBundle {
  const records = Object.freeze([...options.journal.records]);
  const { findings, metrics } = analyzeRecords(records, options.journal.droppedRecords, options.journal.sinkErrors);
  return {
    manifest: {
      formatVersion: 1,
      matchId: options.journal.matchId,
      createdAt: new Date().toISOString(),
      engineVersion: options.engineVersion,
      contentVersion: options.contentVersion,
      assetPackVersion: ASSET_PACK_VERSION,
      rulesetId: options.replay.rulesetId,
      arenaId: options.arenaId,
      seed: options.seed,
      logProfile: options.journal.profile,
      environment: typeof window === 'undefined'
        ? { runtime: 'node', userAgent: `node/${process.version}`, language: 'en', viewport: null }
        : { runtime: 'browser', userAgent: navigator.userAgent, language: navigator.language, viewport: `${innerWidth}x${innerHeight}@${devicePixelRatio}` },
      completeness: { droppedRecords: options.journal.droppedRecords, sinkErrors: options.journal.sinkErrors },
    },
    replay: options.replay,
    records,
    checkpoints: options.checkpoints,
    metrics,
    findings,
    summary: {
      result: options.result,
      critical: findings.filter((item) => item.severity === 'critical').length,
      errors: findings.filter((item) => item.severity === 'error').length,
      warnings: findings.filter((item) => item.severity === 'warning').length,
    },
  };
}
